import { useState } from 'react';

import { Or2DatePicker } from '@/components/date-time/Or2DatePicker';
import type { Or2DialogProps } from '@/components/feedback/Or2Dialog';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { graphql, type DocumentType } from '@/gql';
import { Or2Button } from '@/components/button/Or2Button';
import { useMutation } from '@/hooks/mutationWithSnackbar';

const SET_REVIEW_DATE_FOR_FILE = graphql(`
  mutation SetReviewDateForFile($data: SetReviewDateForFileInput) {
    setReviewDateForFile(params: $data) {
      file {
        ...fileFields
      }
    }
  }
`);

const REVIEW_DATE_FILE_MENU_ITEM_FILE_FRAGMENT = graphql(`
  fragment reviewDateFileMenuItemFileFields on File {
    ...fileFields
  }
`);

type Props = Omit<Or2DialogProps, 'title'> & {
  file: DocumentType<typeof REVIEW_DATE_FILE_MENU_ITEM_FILE_FRAGMENT>;
};

export const ReviewDateFileDialog = ({
  file,
  open,
  onClose,
  ...other
}: Props) => {
  const { _id: fileId, workflow } = file;

  const currentReviewDate = workflow?.reviewDate
    ? new Date(workflow?.reviewDate)
    : null;

  const [reviewDate, setReviewDate] = useState(currentReviewDate);

  const [setReviewDateForFile, { loading }] = useMutation(
    SET_REVIEW_DATE_FOR_FILE,
    {
      onCompleted: onClose,
    },
  );

  return (
    <Or2Dialog
      maxWidth="xs"
      title={`${reviewDate ? 'Change or clear the' : 'Set a'} review date`}
      {...other}
      loading={loading}
      open={open}
      onClose={onClose}
      slotProps={{
        transition: {
          onEnter: () => setReviewDate(currentReviewDate),
        },
      }}
      actions={
        <>
          {currentReviewDate && (
            <Or2Button
              color="error"
              onClick={() => {
                setReviewDateForFile({
                  variables: {
                    data: {
                      file: fileId,
                      date: null,
                    },
                  },
                });
              }}
              sx={{ mr: 'auto' }}
            >
              Clear
            </Or2Button>
          )}
          <Or2Button color="secondary" onClick={onClose}>
            Cancel
          </Or2Button>
          <Or2Button
            color="primary"
            onClick={() => {
              setReviewDateForFile({
                variables: {
                  data: {
                    file: fileId,
                    date: reviewDate ? reviewDate.toISOString() : null,
                  },
                },
              });
            }}
          >
            {reviewDate ? 'Change' : 'Set'}
          </Or2Button>
        </>
      }
    >
      <Or2DatePicker
        label="Review date"
        value={reviewDate}
        slotProps={{ textField: { fullWidth: true } }}
        sx={{ mt: 1 }}
        onChange={(newReviewDate) => setReviewDate(newReviewDate)}
      />
    </Or2Dialog>
  );
};
